/**
 * Branches Management Page
 * إدارة الفروع
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Building, Plus, Edit2, Trash2, ToggleLeft, ToggleRight, X, Check,
  AlertTriangle, Info, Search, MapPin, Clock, Phone,
  RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/catalog';

const COLORS = {
  primary: '#7C3AED',
  primaryLight: '#EDE9FE',
  secondary: '#1B3A6B',
  secondaryLight: '#EEF2F7',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  success: '#059669',
  warning: '#D97706',
  error: '#DC2626',
  info: '#2563EB',
};

function getAuthHeaders() {
  const token = localStorage.getItem('hala_admin_token');
  return { Authorization: `Bearer ${token}` };
}

function Alert({ type = 'info', children, style = {} }) {
  const configs = {
    info: { bg: '#EFF6FF', border: '#3B82F6', color: '#1D4ED8', Icon: Info },
    warning: { bg: '#FFFBEB', border: '#F59E0B', color: '#B45309', Icon: AlertTriangle },
    error: { bg: '#FEF2F2', border: '#EF4444', color: '#B91C1C', Icon: AlertTriangle },
    success: { bg: '#F0FDF4', border: '#22C55E', color: '#15803D', Icon: Check },
  };
  const config = configs[type];
  return (
    <div style={{
      padding: '14px 18px', borderRadius: '10px', backgroundColor: config.bg,
      borderRight: `4px solid ${config.border}`, display: 'flex', alignItems: 'flex-start',
      gap: '12px', ...style
    }}>
      <config.Icon size={18} color={config.color} style={{ flexShrink: 0, marginTop: '2px' }} />
      <div style={{ fontSize: '14px', color: config.color, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function Button({ children, variant = 'primary', size = 'md', icon: Icon, loading, disabled, onClick, style = {} }) {
  const variants = {
    primary: { bg: COLORS.primary, color: '#fff' },
    secondary: { bg: COLORS.secondary, color: '#fff' },
    outline: { bg: 'transparent', color: COLORS.text, border: COLORS.border },
    ghost: { bg: 'transparent', color: COLORS.textMuted },
    danger: { bg: COLORS.error, color: '#fff' },
    success: { bg: COLORS.success, color: '#fff' },
  };
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px', gap: '6px' },
    md: { padding: '10px 18px', fontSize: '14px', gap: '8px' },
    lg: { padding: '12px 24px', fontSize: '15px', gap: '10px' },
  };
  const v = variants[variant];
  const s = sizes[size];
  
  return (
    <button onClick={onClick} disabled={disabled || loading} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      gap: s.gap, padding: s.padding, fontSize: s.fontSize, fontWeight: 600,
      fontFamily: "'Tajawal', sans-serif", borderRadius: '10px',
      border: v.border ? `1.5px solid ${v.border}` : 'none',
      backgroundColor: v.bg, color: v.color, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1, transition: 'all 0.2s', ...style
    }}>
      {loading ? <RefreshCw size={16} className="animate-spin" /> : Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

function Input({ value, onChange, placeholder, type = 'text', style = {} }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={{
      width: '100%', padding: '12px 14px', borderRadius: '10px',
      border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
      fontSize: '14px', fontFamily: "'Tajawal', sans-serif", color: COLORS.text,
      outline: 'none', ...style
    }} />
  );
}

function Select({ value, onChange, options, placeholder, style = {} }) {
  return (
    <select value={value} onChange={onChange} style={{
      width: '100%', padding: '12px 14px', borderRadius: '10px',
      border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
      fontSize: '14px', fontFamily: "'Tajawal', sans-serif", color: COLORS.text,
      cursor: 'pointer', outline: 'none', ...style
    }}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(opt => <option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>)}
    </select>
  );
}

function FormField({ label, required, hint, error, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'block', marginBottom: '8px', fontSize: '14px',
        fontWeight: 600, color: COLORS.text,
      }}>
        {label}
        {required && <span style={{ color: COLORS.error, marginRight: '4px' }}>*</span>}
        {hint && <span style={{ fontWeight: 400, color: COLORS.textMuted, marginRight: '8px', fontSize: '12px' }}>({hint})</span>}
      </label>
      {children}
      {error && <p style={{ margin: '6px 0 0', fontSize: '12px', color: COLORS.error }}>{error}</p>}
    </div>
  );
}

function BranchCard({ branch, onEdit, onToggle, onDelete }) {
  return (
    <div style={{
      padding: '20px', borderRadius: '16px', backgroundColor: COLORS.white,
      border: `1px solid ${COLORS.border}`, opacity: branch.is_active ? 1 : 0.6,
    }}>
      <div style={{ display: 'flex', gap: '16px' }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '14px',
          backgroundColor: `${COLORS.primary}15`, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Building size={26} color={COLORS.primary} />
        </div>
        
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600, color: COLORS.text }}>
              {branch.name_ar}
            </h3>
            {!branch.is_active && (
              <span style={{
                padding: '2px 8px', backgroundColor: '#FEE2E2', borderRadius: '4px',
                fontSize: '11px', color: COLORS.error, fontWeight: 500,
              }}>معطل</span>
            )}
          </div>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '8px' }}>
            {branch.city && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <MapPin size={14} color={COLORS.textMuted} />
                <span style={{ fontSize: '13px', color: COLORS.text }}>{branch.city}</span>
              </div>
            )}
            {branch.address && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Building size={14} color={COLORS.textMuted} />
                <span style={{ fontSize: '13px', color: COLORS.textMuted }}>{branch.address}</span>
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            {branch.working_hours && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Clock size={14} color={COLORS.textMuted} />
                <span style={{ fontSize: '13px', color: COLORS.textMuted }}>{branch.working_hours}</span>
              </div>
            )}
            {branch.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Phone size={14} color={COLORS.textMuted} />
                <span style={{ fontSize: '13px', color: COLORS.textMuted }}>{branch.phone}</span>
              </div>
            )}
          </div>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <button onClick={() => onToggle(branch)} title={branch.is_active ? 'تعطيل' : 'تفعيل'}
            style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: 'transparent',
              color: branch.is_active ? COLORS.success : COLORS.textMuted, cursor: 'pointer' }}>
            {branch.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
          </button>
          <button onClick={() => onEdit(branch)} title="تعديل"
            style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: COLORS.primaryLight,
              color: COLORS.primary, cursor: 'pointer' }}>
            <Edit2 size={16} />
          </button>
          <button onClick={() => onDelete(branch)} title="حذف"
            style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: '#FEE2E2',
              color: COLORS.error, cursor: 'pointer' }}>
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function BranchModal({ isOpen, onClose, onSave, cities, editingItem }) {
  const [formData, setFormData] = useState({
    name_ar: '', name_en: '', city: '', address: '',
    working_hours: '', phone: '', email: '', map_link: '', is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    if (isOpen) {
      if (editingItem) {
        setFormData({
          name_ar: editingItem.name_ar || '',
          name_en: editingItem.name_en || '',
          city: editingItem.city || '',
          address: editingItem.address || '',
          working_hours: editingItem.working_hours || '',
          phone: editingItem.phone || '',
          email: editingItem.email || '',
          map_link: editingItem.map_link || '',
          is_active: editingItem.is_active !== false,
        });
      } else {
        setFormData({
          name_ar: '', name_en: '', city: '', address: '',
          working_hours: '', phone: '', email: '', map_link: '', is_active: true,
        });
      }
      setError('');
    }
  }, [isOpen, editingItem]);
  
  const validate = () => {
    if (!formData.name_ar.trim()) return 'اسم الفرع بالعربية مطلوب';
    if (!formData.city) return 'المدينة مطلوبة';
    return null;
  };
  
  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await onSave(formData);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'حدث خطأ أثناء الحفظ');
    }
    setLoading(false);
  };
  
  if (!isOpen) return null;
  
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }} />
      
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '650px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto',
        backgroundColor: COLORS.white, borderRadius: '16px', boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, backgroundColor: COLORS.white, zIndex: 1,
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: COLORS.text }}>
            {editingItem ? 'تعديل الفرع' : 'إضافة فرع جديد'}
          </h2>
          <button onClick={onClose} style={{
            width: '36px', height: '36px', borderRadius: '8px', border: 'none',
            backgroundColor: COLORS.bg, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={20} color={COLORS.textMuted} />
          </button>
        </div>
        
        <div style={{ padding: '24px' }}>
          {error && <Alert type="error" style={{ marginBottom: '20px' }}>{error}</Alert>}
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="اسم الفرع بالعربية" required>
              <Input value={formData.name_ar} onChange={e => setFormData({ ...formData, name_ar: e.target.value })}
                placeholder="مثال: فرع الرياض - العليا" />
            </FormField>
            <FormField label="اسم الفرع بالإنجليزية">
              <Input value={formData.name_en} onChange={e => setFormData({ ...formData, name_en: e.target.value })}
                placeholder="Example: Riyadh - Olaya Branch" />
            </FormField>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="المدينة" required>
              <Select value={formData.city}
                onChange={e => setFormData({ ...formData, city: e.target.value })}
                placeholder="-- اختر المدينة --"
                options={cities.map(c => ({ value: c.name_ar, label: c.name_ar }))} />
            </FormField>
            <FormField label="أوقات العمل">
              <Input value={formData.working_hours} onChange={e => setFormData({ ...formData, working_hours: e.target.value })}
                placeholder="مثال: الأحد - الخميس 9ص - 5م" />
            </FormField>
          </div>
          
          <FormField label="العنوان">
            <Input value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })}
              placeholder="العنوان التفصيلي للفرع" />
          </FormField>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="رقم الهاتف">
              <Input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })}
                placeholder="مثال: 0112345678" />
            </FormField>
            <FormField label="البريد الإلكتروني">
              <Input value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder="branch@example.com" />
            </FormField>
          </div>
          
          <FormField label="رابط الموقع على الخريطة">
            <Input value={formData.map_link} onChange={e => setFormData({ ...formData, map_link: e.target.value })}
              placeholder="https://maps.google.com/..." />
          </FormField>
        </div>
        
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${COLORS.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: '12px', backgroundColor: COLORS.bg,
          position: 'sticky', bottom: 0,
        }}>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button variant="success" icon={Check} onClick={handleSubmit} loading={loading}>
            {editingItem ? 'حفظ التعديلات' : 'إضافة الفرع'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function BranchesPage() {
  const [branches, setBranches] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCity, setFilterCity] = useState('');
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch cities and branches from API
      const [citiesRes, branchesRes] = await Promise.all([
        axios.get(`${API}/cities`, { headers: getAuthHeaders() }),
        axios.get(`${API}/branches`, { headers: getAuthHeaders() }),
      ]);
      setCities(citiesRes.data || []);
      setBranches(branchesRes.data || []);
    } catch (e) {
      console.error('Failed to fetch:', e);
    }
    setLoading(false);
  }, []);
  
  useEffect(() => { fetchData(); }, [fetchData]);
  
  const handleSave = async (data) => {
    try {
      if (editingItem) {
        await axios.put(`${API}/branches/${editingItem.branch_id}`, data, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/branches`, data, { headers: getAuthHeaders() });
      }
      fetchData();
      setEditingItem(null);
    } catch (e) {
      console.error('Failed to save:', e);
      alert(e.response?.data?.detail || 'فشل في حفظ الفرع');
    }
  };
  
  const handleToggle = async (branch) => {
    try {
      await axios.post(`${API}/branches/${branch.branch_id}/toggle`, {}, { headers: getAuthHeaders() });
      fetchData();
    } catch (e) {
      console.error('Failed to toggle:', e);
    }
  };
  
  const handleDelete = async (branch) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${branch.name_ar}"؟`)) return;
    try {
      await axios.delete(`${API}/branches/${branch.branch_id}`, { headers: getAuthHeaders() });
      fetchData();
    } catch (e) {
      console.error('Failed to delete:', e);
      alert(e.response?.data?.detail || 'فشل في حذف الفرع');
    }
  };
  
  const filtered = branches.filter(b => {
    if (filterCity && b.city !== filterCity) return false;
    if (searchQuery && !b.name_ar?.includes(searchQuery)) return false;
    return true;
  });
  
  const activeCount = branches.filter(b => b.is_active).length;
  
  return (
    <div data-testid="branches-page" style={{ padding: '24px', backgroundColor: COLORS.bg, minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '12px',
            backgroundColor: `${COLORS.primary}15`, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Building size={24} color={COLORS.primary} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 700, color: COLORS.text }}>الفروع</h1>
            <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
              إدارة فروع الخدمات
            </p>
          </div>
        </div>
      </div>
      
      <Alert type="info" style={{ marginBottom: '24px' }}>
        الفروع تُستخدم لتحديد مواقع تقديم الخدمات الحضورية. يمكنك إضافة تفاصيل كل فرع هنا.
      </Alert>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'إجمالي الفروع', value: branches.length, color: COLORS.primary },
          { label: 'فروع نشطة', value: activeCount, color: COLORS.success },
          { label: 'فروع معطلة', value: branches.length - activeCount, color: COLORS.warning },
        ].map((stat, i) => (
          <div key={i} style={{
            padding: '20px', borderRadius: '12px', backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: '14px', color: COLORS.textMuted }}>{stat.label}</div>
          </div>
        ))}
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <Search size={18} color={COLORS.textMuted} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="بحث بالاسم..."
            style={{ paddingRight: '40px' }} />
        </div>
        <Select value={filterCity} onChange={e => setFilterCity(e.target.value)}
          placeholder="كل المدن"
          options={cities.map(c => ({ value: c.name_ar, label: c.name_ar }))}
          style={{ width: '180px' }} />
        <Button data-testid="add-branch-btn" variant="primary" icon={Plus} onClick={() => { setEditingItem(null); setShowModal(true); }}>
          إضافة فرع
        </Button>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: COLORS.textMuted }}>
            <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
            <p>جاري التحميل...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px', backgroundColor: COLORS.white,
            borderRadius: '16px', border: `1px solid ${COLORS.border}`,
          }}>
            <Building size={48} color={COLORS.textMuted} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: COLORS.text }}>
              لا توجد فروع
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: COLORS.textMuted }}>
              {searchQuery || filterCity ? 'لا توجد نتائج تطابق البحث' : 'قم بإضافة فروع جديدة'}
            </p>
            <Button variant="primary" icon={Plus} onClick={() => { setEditingItem(null); setShowModal(true); }}>
              إضافة أول فرع
            </Button>
          </div>
        ) : (
          filtered.map(b => (
            <BranchCard key={b.branch_id} branch={b}
              onEdit={(item) => { setEditingItem(item); setShowModal(true); }}
              onToggle={handleToggle} onDelete={handleDelete} />
          ))
        )}
      </div>
      
      <BranchModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingItem(null); }}
        onSave={handleSave}
        cities={cities}
        editingItem={editingItem}
      />
    </div>
  );
}
